import { getAttendanceWorksiteIconName, getPersonioAttendanceTypeIcon } from "@msuite/ui/icons";
import { Link } from "@msuite/ui/link";
import { localized } from "@msuite/ui/localization";
import {
	createColumnHelper,
	createMeta,
	createRowActions,
	createSubRowHandle,
} from "@msuite/ui/table";
import { IconTag, Tag } from "@msuite/ui/tag";
import {
	getAttendanceStateColor,
	getAttendanceStateTranslationKey,
	getAttendanceWorksiteColor,
	getAttendanceWorksiteTranslationKey,
} from "@msuite/utils/attendances";
import { getColorByString } from "@msuite/utils/colors";
import { getCalculatedTimeEasyString, moment } from "@msuite/utils/date";
import { calculateAttendancesTime } from "@msuite/utils/integrations";
import { getAttendanceTypeTranslationKey } from "@msuite/utils/attendances";
import { guardString } from "@msuite/utils/strings";
import type { OrganizationAttendanceListItem } from "@msuite/utils/types";
import { NoteText } from "@/components/notes";
import { RowActions } from "./row-actions";

const columnHelper = createColumnHelper<OrganizationAttendanceListItem>();

export const columns = [
	createSubRowHandle<OrganizationAttendanceListItem>(),
	columnHelper.accessor((row) => moment(row.startTime).db(), {
		id: "date",
		header: localized("dates.date"),
		cell: (cell) => moment(cell.getValue()).dateFormat(),
	}),
	columnHelper.accessor("userName", {
		id: "userName",
		header: localized("general.name"),
		cell: (cell) => cell.getValue(),
	}),
	columnHelper.accessor("startTime", {
		id: "startTime",
		header: localized("dates.start_time"),
		cell: (cell) => (
			<Tag
				color="emerald"
				variant="plain"
			>
				{moment(cell.getValue()).getTimeString()}
			</Tag>
		),
		meta: createMeta({
			isTabular: true,
		}),
	}),
	columnHelper.accessor("endTime", {
		id: "endTime",
		header: localized("dates.end_time"),
		cell: (cell) =>
			cell.getValue() ? (
				<Tag
					color="red"
					variant="plain"
				>
					{moment(cell.getValue()).getTimeString()}
				</Tag>
			) : (
				guardString(null)
			),
		meta: createMeta({
			isTabular: true,
		}),
	}),
	columnHelper.accessor((row) => getCalculatedTimeEasyString(calculateAttendancesTime([row])), {
		id: "time",
		header: localized("dates.time"),
		cell: (cell) => cell.getValue(),
	}),
	columnHelper.accessor("type", {
		id: "type",
		header: localized("general.type"),
		cell: (cell) => (
			<IconTag
				iconName={getPersonioAttendanceTypeIcon(cell.getValue())}
				color={getColorByString(cell.getValue())}
			>
				{localized(getAttendanceTypeTranslationKey(cell.row.original))}
			</IconTag>
		),
	}),
	columnHelper.accessor((row) => localized(getAttendanceWorksiteTranslationKey(row.worksite)), {
		id: "worksite",
		header: localized("users.worksite"),
		cell: (cell) =>
			cell.row.original.type === "break" ? (
				guardString(null)
			) : (
				<IconTag
					iconName={getAttendanceWorksiteIconName(cell.row.original.worksite)}
					color={getAttendanceWorksiteColor(cell.row.original.worksite)}
				>
					{localized(getAttendanceWorksiteTranslationKey(cell.row.original.worksite))}
				</IconTag>
			),
	}),
	columnHelper.accessor("projectName", {
		id: "project",
		header: localized("projects.project"),
		cell: (cell) =>
			cell.getValue() ? (
				<Link href={`/projects/${cell.row.original.projectId}`}>
					{guardString(cell.getValue())}
				</Link>
			) : (
				guardString(null)
			),
	}),
	columnHelper.accessor((row) => localized(getAttendanceStateTranslationKey(row.state)), {
		id: "state",
		header: localized("general.state"),
		cell: (cell) => (
			<Tag
				variant="plain"
				color={getAttendanceStateColor(cell.row.original.state)}
			>
				{cell.getValue()}
			</Tag>
		),
	}),
	columnHelper.accessor((row) => row.rejectedReason, {
		id: "rejectedReason",
		header: localized("users.reject_attendance_reason"),
		cell: (cell) => (
			<NoteText
				note={guardString(cell.getValue())}
				withPopover
			/>
		),
	}),
	createRowActions(RowActions),
];
